import React, { useState } from 'react';
import { AnimatePresence } from 'framer-motion';
import { CheckIcon } from 'lucide-react';
import { LegalDocModal } from './LegalDocModal';
import { legalDocs, LegalDocId } from '../../data/legal';
interface ConsentCheckboxProps {
  checked: boolean;
  onChange: (checked: boolean) => void;
  /** The documents being agreed to, joined as "A and B". */
  docs: LegalDocId[];
  /** Lead-in before the document links. */
  lead?: string;
  /** Briefly highlight when someone tries to continue without ticking. */
  attention?: boolean;
  /** 'talab' uses Talab indigo; 'merchant' uses the shop's brand variables. */
  tone?: 'talab' | 'merchant';
  className?: string;
}

/**
 * An explicit, unticked agreement with each document readable in place.
 * Links open the text without toggling the box, so reading never counts
 * as agreeing.
 */
export function ConsentCheckbox({
  checked,
  onChange,
  docs,
  lead = 'I agree to Talab’s',
  attention = false,
  tone = 'talab',
  className = ''
}: ConsentCheckboxProps) {
  const [open, setOpen] = useState<LegalDocId | null>(null);
  const merchant = tone === 'merchant';
  const boxOn = merchant ? 'bg-[var(--mp)] border-[var(--mp)] text-[var(--mp-t)]' : 'bg-primary-indigo border-primary-indigo text-white';
  const link = merchant ? 'text-[var(--mp-deep)]' : 'text-primary-indigo';
  return <>
      <label className={`flex items-start gap-3 cursor-pointer select-none rounded-xl transition-colors duration-150 ease-out ${attention && !checked ? 'bg-error/[0.06] ring-1 ring-error/30 -mx-2 px-2 py-2' : ''} ${className}`} data-id="element-2567" data-mp="3gn7ph6tql40a">
        <input type="checkbox" checked={checked} onChange={e => onChange(e.target.checked)} className="peer sr-only" data-id="element-2568" data-mp="37tvs4mojwlky" />
        <span aria-hidden="true" className={`mt-[1px] w-[18px] h-[18px] rounded-[5px] border flex items-center justify-center flex-shrink-0 transition-colors duration-150 ease-out peer-focus-visible:ring-2 peer-focus-visible:ring-offset-2 peer-focus-visible:ring-primary-indigo/50 ${checked ? boxOn : attention ? 'bg-white border-error/60' : 'bg-white border-slate-300'}`} data-id="element-2569" data-mp="45kdcy42hepx">
          {checked && <CheckIcon className="w-3 h-3" strokeWidth={3} data-id="element-2570" data-mp="9ju29kzkaz8b" />}
        </span>
        <span className="text-[13px] text-slate-600 leading-snug text-left" data-id="element-2571" data-mp="dkmacawp6y30">
          {lead}{' '}
          {docs.map((id, i) => <React.Fragment key={id} data-id="element-2572" data-mp="3vp7ggr8mahrt" data-mpi={i}>
              {i > 0 && (i === docs.length - 1 ? ' and ' : ', ')}
              <button type="button" onClick={e => {
            e.preventDefault();
            e.stopPropagation();
            setOpen(id);
          }} className={`font-semibold underline underline-offset-2 decoration-current/30 hover:decoration-current ${link}`} data-id="element-2573" data-mp="4kqxm1nhuv0v">
                {legalDocs[id].title}
              </button>
            </React.Fragment>)}
          .
        </span>
      </label>

      <AnimatePresence data-id="element-2574" data-mp="39iv9ysfpacfy">
        {open && <LegalDocModal doc={open} onClose={() => setOpen(null)} data-id="element-2575" data-mp="39fqzxefk0mhd" />}
      </AnimatePresence>
    </>;
}